import { FC, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import { useGetAdminPlans, TPlan } from "../../api/billing";
import {
  formatCents,
  useGetPlanPriceHistory,
  useUpdatePlanPrice,
} from "../../api/billingAdmin";
import { useAuth } from "../../auth/useAuth";

type EditTarget = {
  plan: TPlan;
  cycle: "monthly" | "annual";
  currentCents: number;
};

/**
 * Plan pricing.
 *
 * Stripe Price objects are immutable, so saving here does not edit a price —
 * it creates a new Stripe Price, repoints the plan and archives the old one.
 * Customers already subscribed keep the price they signed up on, which the
 * dialog states explicitly before the admin confirms.
 */
export const BillingPricingTab: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { can } = useAuth();

  /*
   * Repricing rewrites Stripe Price objects and is gated server-side on
   * ONLY_SUPER_ADMIN. Mirroring that here turns a 403 discovered AFTER filling
   * in the form into a disabled button with a reason attached.
   *
   * Viewing and editing are separate capabilities on purpose: a finance role
   * that may audit tariffs without changing them is then a ROLE_PERMISSIONS
   * entry rather than a code change.
   */
  const canManagePricing = can("billing.pricing.update");

  const { plans, isLoading } = useGetAdminPlans();
  const [target, setTarget] = useState<EditTarget | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const updatePrice = useUpdatePlanPrice(() => setTarget(null));
  const { history } = useGetPlanPriceHistory(target?.plan.id ?? 0, !!target);

  const openEditor = (plan: TPlan, cycle: "monthly" | "annual") => {
    if (!canManagePricing) return;
    const cents = centsFor(plan, cycle);
    setTarget({ plan, cycle, currentCents: cents });
    setAmount((cents / 100).toFixed(2));
    setReason("");
  };

  // The API works in cents; the form works in euros. Parsing here rather than
  // in the mutation keeps the rounding decision in one visible place.
  const parsedCents = useMemo(() => {
    const value = parseFloat(amount.replace(",", "."));
    if (!Number.isFinite(value)) return NaN;
    return Math.round(value * 100);
  }, [amount]);

  const amountError =
    !Number.isFinite(parsedCents) || parsedCents < 1 || parsedCents > 100000000;

  const unchanged = target !== null && parsedCents === target.currentCents;

  const handleSubmit = () => {
    // Belt and braces: the dialog cannot be opened without the permission, but
    // a stale render or a devtools poke must not reach the mutation either.
    if (!canManagePricing) return;
    if (!target || amountError || unchanged) return;
    updatePrice.mutate({
      planId: target.plan.id,
      billing_cycle: target.cycle,
      price_cents: parsedCents,
      reason: reason.trim() || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className={classes.emptyState}>
        <CircularProgress size={28} />
      </div>
    );
  }

  const saasPlans = plans.filter((p) => p.tier !== "ai_addon");
  const addonPlans = plans.filter((p) => p.tier === "ai_addon");

  return (
    <>
      <div className={classes.infoBox}>
        {t(
          "billing.pricingInfo",
          "Modifier un tarif crée un nouveau tarif chez Stripe et archive l'ancien. Les abonnés actuels conservent leur tarif d'origine jusqu'à migration explicite."
        )}
      </div>

      {!canManagePricing && (
        <div className={classes.warningBox} role="status">
          <span>
            {t(
              "billing.pricingReadOnly",
              "Lecture seule : la modification des tarifs est réservée aux super-administrateurs."
            )}
          </span>
        </div>
      )}

      {renderGroup(t("billing.saasPlans", "Offres"), saasPlans)}
      {addonPlans.length > 0 &&
        renderGroup(t("billing.aiAddons", "Options IA"), addonPlans)}

      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        className={classes.dialog}
        fullWidth
      >
        <DialogTitle>
          {t("billing.changePrice", "Modifier le tarif")}
          {target ? ` — ${target.plan.name}` : ""}
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.warningBox}>
            <span>
              {t(
                "billing.priceWarning",
                "Le nouveau tarif s'appliquera uniquement aux nouvelles souscriptions. Les abonnements en cours continueront d'être facturés au tarif actuel."
              )}
            </span>
          </div>

          <div className={classes.formRow}>
            <TextField
              label={t("billing.newPrice", "Nouveau tarif")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={amountError}
              helperText={
                amountError
                  ? t("billing.priceInvalid", "Montant invalide")
                  : t("billing.pricePerVehicle", "Par véhicule, hors taxes")
              }
              InputProps={{ endAdornment: <span>€</span> }}
              autoFocus
            />
            <TextField
              label={t("billing.currentPrice", "Tarif actuel")}
              value={
                target ? (target.currentCents / 100).toFixed(2) + " €" : ""
              }
              disabled
            />
          </div>

          <TextField
            label={t("billing.priceReason", "Motif (journalisé)")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            placeholder={t(
              "billing.priceReasonPlaceholder",
              "Ex. révision tarifaire annuelle 2026"
            )}
          />

          {history.length > 0 && (
            <div>
              <p className={classes.cardHint}>
                {t("billing.priceHistory", "Historique des tarifs")}
              </p>
              {history.slice(0, 5).map((row) => (
                <div key={row.id} className={classes.priceRow}>
                  <span className={classes.priceCycle}>
                    {new Date(row.created_at).toLocaleDateString()} —{" "}
                    {row.billing_cycle === "monthly"
                      ? t("billing.monthly", "Mensuel")
                      : t("billing.annual", "Annuel")}
                  </span>
                  <span>
                    {row.old_price_cents !== null
                      ? `${formatCents(row.old_price_cents, row.currency)} → `
                      : ""}
                    <strong>
                      {formatCents(row.new_price_cents, row.currency)}
                    </strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setTarget(null)}>
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !canManagePricing || amountError || unchanged || updatePrice.isPending
            }
            startIcon={
              updatePrice.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t("billing.confirmPriceChange", "Confirmer le nouveau tarif")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  function renderGroup(title: string, list: TPlan[]) {
    if (!list.length) return null;

    return (
      <div style={{ marginBottom: 24 }}>
        <div className={classes.cardTitle}>
          <h3>{title}</h3>
        </div>
        <div className={classes.planGrid}>
          {list.map((plan) => (
            <div key={plan.id} className={classes.planCard}>
              <div className={classes.planCardHeader}>
                <div style={{ minWidth: 0 }}>
                  <h4 className={classes.planName}>{plan.name}</h4>
                  <span className={classes.planSlug}>{plan.slug}</span>
                </div>
                <Chip
                  size="small"
                  label={
                    plan.is_active
                      ? t("common.active", "Actif")
                      : t("common.inactive", "Inactif")
                  }
                  color={plan.is_active ? "success" : "default"}
                />
              </div>

              {(["monthly", "annual"] as const).map((cycle) => (
                <div key={cycle} className={classes.priceRow}>
                  <div>
                    <div className={classes.priceValue}>
                      {(centsFor(plan, cycle) / 100).toFixed(2)} €
                    </div>
                    <div className={classes.priceCycle}>
                      {cycle === "monthly"
                        ? t("billing.monthly", "Mensuel")
                        : t("billing.annual", "Annuel")}
                    </div>
                    {/* Both fields are per-MONTH rates - the annual one is the
                        discounted rate for committing to a year, and is charged
                        x 12. Spelling this out here because it is the one place
                        a human types these numbers, and reading the annual field
                        as a yearly total is what put the AI add-ons 12x out. */}
                    <div className={classes.priceHint}>
                      {cycle === "monthly"
                        ? t(
                            "billing.monthlyHint",
                            plan.tier === "ai_addon"
                              ? "€ / société / mois"
                              : "€ / véhicule / mois"
                          )
                        : t(
                            "billing.annualHint",
                            plan.tier === "ai_addon"
                              ? `€ / société / mois, facturé annuellement (soit ${(
                                  (centsFor(plan, "annual") * 12) / 100
                                ).toFixed(2)} € / an)`
                              : "€ / véhicule / mois, facturé annuellement (× 12)"
                          )}
                    </div>
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!canManagePricing}
                    title={
                      canManagePricing
                        ? undefined
                        : t(
                            "billing.pricingReadOnlyHint",
                            "Réservé aux super-administrateurs"
                          )
                    }
                    onClick={() => openEditor(plan, cycle)}
                  >
                    {t("common.edit", "Modifier")}
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }
};

/**
 * The admin plans endpoint exposes `price_monthly` / `price_annual` as decimal
 * accessors while the write side works in cents, so normalise on read.
 */
const centsFor = (plan: any, cycle: "monthly" | "annual"): number => {
  const cents = plan?.[`price_cents_${cycle}`];
  if (Number.isFinite(Number(cents))) return Number(cents);

  const decimal = plan?.[`price_${cycle}`];
  return Number.isFinite(Number(decimal)) ? Math.round(Number(decimal) * 100) : 0;
};

export default BillingPricingTab;
